import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { LogEventParams } from '@/types/audit';
import { debugLogger } from '@/utils/debugLogger';

export const useAuditLog = () => {
  const [loading, setLoading] = useState(false);

  const logEvent = async (params: LogEventParams) => {
    setLoading(true);
    try {
      const { event_type, user_id, metadata, location_data, session_id } = params;

      debugLogger.debug('audit', 'Logging event', { event_type, user_id });

      // For login events, use the edge function to capture IP and user agent
      if (event_type === 'login') {
        const { data, error } = await supabase.functions.invoke('audit-login', {
          body: {
            user_id,
            session_id,
            location_data,
          },
        });

        if (error) {
          debugLogger.error('AuditLog: Error logging login event via edge function', error);
          throw error;
        }

        debugLogger.debug('audit', 'Login event logged successfully', data);
        return { success: true, data };
      }

      // For location events (update and skip), use the edge function to capture IP and user agent
      if (event_type === 'location_update' || event_type === 'location_skip') {
        const { data, error } = await supabase.functions.invoke('audit-location', {
          body: {
            event_type,
            user_id,
            old_location: (location_data as any)?.old_location,
            new_location: (location_data as any)?.new_location,
            metadata,
          },
        });

        if (error) {
          debugLogger.error('AuditLog: Error logging location event via edge function', error);
          throw error;
        }

        debugLogger.debug('audit', 'Location event logged successfully', data);
        return { success: true, data };
      }

      // For other event types, insert directly into audit_logs
      const { error } = await supabase.from('audit_logs').insert({
        user_id,
        event_type,
        metadata: metadata || {},
        location_data,
        session_id,
      });

      if (error) {
        debugLogger.error('AuditLog: Error logging event', error);
        throw error;
      }

      debugLogger.debug('audit', 'Event logged successfully', { event_type, user_id });
      return { success: true };

    } catch (error) {
      debugLogger.error('AuditLog: Unexpected error', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return {
    logEvent,
    loading,
  };
};
